// src/screens/OnboardingTerms.jsx
import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { FiCheck } from "react-icons/fi";

import { apiFetch } from "../api";
import { useAuth } from "../auth/AuthProvider";
import TermsText from "../components/legal/TermsText";

export default function OnboardingTerms() {
  const nav = useNavigate();
  const { auth } = useAuth();

  const scrollRef = useRef(null);

  const [scrolledToEnd, setScrolledToEnd] = useState(false);
  const [accepted, setAccepted] = useState(false);
  const [loading, setLoading] = useState(false);

  // ✅ versão corrente (vinda do backend)
  const [requiredVersion, setRequiredVersion] = useState(null);

  const customerId = auth?.customerId ?? null;

  const canAccept = useMemo(() => {
    return !!customerId && scrolledToEnd && accepted && !loading;
  }, [customerId, scrolledToEnd, accepted, loading]);

  /* ============================================================
     Scroll obrigatório
  ============================================================ */
  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;

    const check = () => {
      const nearBottom = el.scrollTop + el.clientHeight >= el.scrollHeight - 8;
      if (nearBottom) setScrolledToEnd(true);
    };

    el.addEventListener("scroll", check);
    setTimeout(check, 0);

    return () => el.removeEventListener("scroll", check);
  }, []);

  /* ============================================================
     Busca versão corrente (1x)
  ============================================================ */
  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const s = await apiFetch("/api/onboarding/status");
        if (!alive) return;
        const v = String(s?.requiredVersion || "").trim();
        setRequiredVersion(v || null);
      } catch {
        if (!alive) return;
        setRequiredVersion(null);
      }
    })();
    return () => {
      alive = false;
    };
  }, []);

  /* ============================================================
     Aceite
  ============================================================ */
  async function handleAccept() {
    // ✅ não deixa aceitar sem sessão hidratada (evita cacheKey nulo e loop)
    if (!customerId) {
      alert("Aguarde um instante: carregando sua sessão...");
      return;
    }
    if (!scrolledToEnd || !accepted || loading) return;

    setLoading(true);
    try {
      const v = requiredVersion; // pode ser null em falha de rede

      // ✅ envia version quando disponível (contrato explícito)
      await apiFetch("/api/onboarding/accept-terms", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          ...(v ? { version: v } : {}),
          acceptedAt: new Date().toISOString(),
        }),
      });

      // ✅ cache legado SEMPRE (garante compat e fallback)
      const legacyKey = `termsAccepted:${customerId}`;
      sessionStorage.setItem(legacyKey, "1");
      localStorage.setItem(legacyKey, "1");

      // ✅ cache versionado quando souber a versão
      if (v) {
        const versionedKey = `termsAccepted:${customerId}:${v}`;
        sessionStorage.setItem(versionedKey, "1");
        localStorage.setItem(versionedKey, "1");
      }

      // ✅ destrava o guard imediatamente (mesma aba)
      window.dispatchEvent(
        new CustomEvent("finbot_terms_accepted", {
          detail: { customerId, requiredVersion: v || null },
        })
      );

      // segue para o app
      nav("/today", { replace: true });
    } catch (e) {
      console.error(e);
      alert("Erro ao registrar aceite. Tente novamente.");
    } finally {
      setLoading(false);
    }
  }

  /* ============================================================
     Render
  ============================================================ */
  return (
    <div style={{ padding: 16, maxWidth: 860, margin: "0 auto" }}>
      <h1 style={{ marginBottom: 6 }}>
        Termos de Uso e Política de Privacidade
      </h1>

      <p style={{ opacity: 0.8, marginBottom: 14 }}>
        Leia atentamente antes de continuar.
      </p>

      {/* Área de leitura obrigatória */}
      <div
        ref={scrollRef}
        style={{
          height: "55vh",
          overflowY: "auto",
          padding: 16,
          borderRadius: 10,
          background: "rgba(255,255,255,0.05)",
          border: "1px solid rgba(255,255,255,0.12)",
          marginBottom: 12,
        }}
      >
        <TermsText />
      </div>

      {!scrolledToEnd && (
        <div style={{ fontSize: 13, opacity: 0.7, marginBottom: 14 }}>
          Role até o final para liberar o aceite.
        </div>
      )}

      {/* Checkbox */}
      <label
        style={{
          display: "flex",
          alignItems: "center",
          gap: 10,
          marginBottom: 20,
          opacity: scrolledToEnd ? 1 : 0.45,
          pointerEvents: scrolledToEnd ? "auto" : "none",
          userSelect: "none",
        }}
      >
        <input
          type="checkbox"
          checked={accepted}
          onChange={(e) => setAccepted(e.target.checked)}
        />
        <span>
          Li e concordo com os Termos de Uso e a Política de Privacidade
        </span>
      </label>

      {/* CTA */}
      <button
        onClick={handleAccept}
        disabled={!canAccept}
        style={{
          width: "100%",
          padding: "14px 16px",
          borderRadius: 10,
          border: "none",
          fontWeight: 700,
          cursor: !canAccept ? "not-allowed" : "pointer",
          background: !canAccept ? "rgba(255,255,255,0.22)" : "#4CAF50",
          color: "#000",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          gap: 8,
        }}
      >
        <FiCheck />
        {loading ? "Salvando..." : "Concordar e continuar"}
      </button>
    </div>
  );
}
